import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import JsonLd, { buildFaqSchema } from '../components/JsonLd';
import Newsletters from './Newsletters';
import { MOBILE_APP_PAGE } from '../data/serviceLandingPages';
import '../styles/mobile-app-premium.css';

const {
  seo,
  eyebrow,
  title,
  lead,
  badges,
  services,
  process,
  deliverables,
  relatedLinks,
  faqs,
  primaryCta,
  secondaryCta,
} = MOBILE_APP_PAGE;

const DEVICES = [
  {
    id: 'ios',
    label: 'iOS',
    image: '/img/mobile/elite-seva-home.png',
    alt: 'Elite Seva mobile app home screen with vehicle wash services',
  },
  {
    id: 'android',
    label: 'Android',
    image: '/img/mobile/elite-seva-role.png',
    alt: 'Elite Seva mobile app role selection for customer and executive',
  },
];

const SHOWCASE_APPS = [
  {
    id: 'elite-seva',
    title: 'Elite Seva',
    blurb:
      'Vehicle services marketplace with role-based access, daily and on-demand washes, bookings, and ops-ready flows.',
    tags: ['React Native', 'Bookings', 'Role-based'],
    screens: [
      {
        src: '/img/mobile/elite-seva-home.png',
        alt: 'Elite Seva app home with car and bike wash services',
        frame: 'ios' as const,
      },
      {
        src: '/img/mobile/elite-seva-role.png',
        alt: 'Elite Seva role selection for customer and executive',
        frame: 'android' as const,
      },
    ],
    reveal: 'left' as const,
  },
  {
    id: 'wecharge',
    title: 'We Charge',
    blurb:
      'EV charging discovery and booking—station maps, session flows, and store-ready iOS and Android releases.',
    tags: ['React Native', 'Maps', 'Store release'],
    screens: [
      {
        src: '/img/wecharge-app.png',
        alt: 'We Charge Android app for EV charging station booking',
        frame: 'android' as const,
      },
    ],
    reveal: 'right' as const,
  },
];

const PLATFORM_STRIP = [
  { label: 'iOS', note: 'App Store ready' },
  { label: 'Android', note: 'Play Console ready' },
  { label: 'React Native', note: 'Shared codebase' },
];

const OUTCOMES = [
  { value: '1 codebase', label: 'Ship iOS and Android together' },
  { value: 'Store-ready', label: 'Certificates, signing, and review prep' },
  { value: 'API-connected', label: 'CRM, ecommerce, and ops backends' },
  { value: 'Post-launch', label: 'Updates, monitoring, and iteration' },
];

function MobileApp() {
  const pageRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;

    const nodes = root.querySelectorAll('.map-reveal');
    if (!nodes.length) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      nodes.forEach((node) => node.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.16, rootMargin: '0px 0px -8% 0px' }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  const faqSchema = buildFaqSchema(faqs);

  return (
    <>
      <SeoHead
        {...seo}
        image="/img/mobile/elite-seva-home.png"
        imageAlt="Elite Seva and React Native mobile apps for iOS and Android"
      />
      {faqSchema ? <JsonLd data={faqSchema} /> : null}

      <main className="mobile-app-premium" ref={pageRef}>
        <div className="header-spacer" />

        <section className="map-hero" aria-labelledby="map-hero-title">
          <div className="map-shell">
            <div className="map-hero__layout">
              <div className="map-hero__copy map-reveal map-reveal--up">
                <p className="map-kicker">{eyebrow}</p>
                <h1 id="map-hero-title">{title}</h1>
                <p className="map-hero__lead">{lead}</p>
                <div className="map-hero__actions">
                  <Link to={primaryCta.to} className="map-btn map-btn--primary">
                    {primaryCta.label}
                  </Link>
                  <Link to={secondaryCta.to} className="map-btn map-btn--ghost">
                    {secondaryCta.label}
                  </Link>
                </div>
                <ul className="map-hero__badges" aria-label="Service highlights">
                  {badges.map((badge) => (
                    <li key={badge}>{badge}</li>
                  ))}
                </ul>
              </div>

              <div className="map-devices-stage map-reveal map-reveal--right">
                <p className="map-devices-stage__badge">Featured app · Elite Seva</p>
                <div className="map-devices" aria-label="Elite Seva iOS and Android app previews">
                  <div className="map-devices__glow" aria-hidden="true" />
                  {DEVICES.map((device, index) => (
                    <figure
                      key={device.id}
                      className={`map-device map-device--${device.id} map-reveal ${
                        index === 0 ? 'map-reveal--left' : 'map-reveal--right'
                      }`}
                      style={{ transitionDelay: `${120 + index * 90}ms` }}
                    >
                      <div className={`map-device__frame map-device__frame--${device.id}`}>
                        <div className="map-device__notch" aria-hidden="true" />
                        <img src={device.image} alt={device.alt} loading="eager" />
                        <div className="map-device__home" aria-hidden="true" />
                      </div>
                      <figcaption className="map-device__label">{device.label}</figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="map-platforms" aria-label="Supported platforms">
          <div className="map-shell">
            <ul className="map-platforms__list">
              {PLATFORM_STRIP.map((item, index) => (
                <li
                  key={item.label}
                  className={`map-platforms__item map-reveal ${
                    index % 2 === 0 ? 'map-reveal--left' : 'map-reveal--right'
                  }`}
                  style={{ transitionDelay: `${index * 70}ms` }}
                >
                  <strong>{item.label}</strong>
                  <span>{item.note}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="map-outcomes" aria-labelledby="map-outcomes-title">
          <div className="map-shell">
            <div className="map-section__header map-reveal map-reveal--up">
              <p className="map-kicker">What teams get</p>
              <h2 id="map-outcomes-title">Built to launch—and keep shipping</h2>
            </div>
            <ul className="map-outcomes__list">
              {OUTCOMES.map((item, index) => (
                <li
                  key={item.value}
                  className={`map-outcomes__item map-reveal ${
                    index % 2 === 0 ? 'map-reveal--left' : 'map-reveal--right'
                  }`}
                  style={{ transitionDelay: `${index * 80}ms` }}
                >
                  <strong>{item.value}</strong>
                  <span>{item.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="map-section" aria-labelledby="map-offerings-title">
          <div className="map-shell">
            <div className="map-section__header map-reveal map-reveal--up">
              <p className="map-kicker">What we deliver</p>
              <h2 id="map-offerings-title">Mobile products for customers and operations</h2>
              <p>
                From storefront companions to field tools, we ship React Native apps that connect to
                your APIs, CRM, and ecommerce stack.
              </p>
            </div>
            <div className="map-offerings">
              {services.map((service, index) => (
                <article
                  key={service.title}
                  className={`map-offering map-reveal ${
                    index % 2 === 0 ? 'map-reveal--left' : 'map-reveal--right'
                  }`}
                  style={{ transitionDelay: `${(index % 3) * 70}ms` }}
                >
                  <span className="map-offering__index">{String(index + 1).padStart(2, '0')}</span>
                  <h3>{service.title}</h3>
                  <p>{service.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="map-section map-section--muted" aria-labelledby="map-process-title">
          <div className="map-shell">
            <div className="map-section__header map-reveal map-reveal--up">
              <p className="map-kicker">How we work</p>
              <h2 id="map-process-title">A clear path from idea to store release</h2>
            </div>
            <ol className="map-process">
              {process.map((step, index) => (
                <li
                  key={step.title}
                  className={`map-process__item map-reveal map-reveal--up`}
                  style={{ transitionDelay: `${index * 90}ms` }}
                >
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="map-section" aria-labelledby="map-deliverables-title">
          <div className="map-shell">
            <div className="map-section__header map-reveal map-reveal--up">
              <p className="map-kicker">Deliverables</p>
              <h2 id="map-deliverables-title">What you leave with</h2>
            </div>
            <ul className="map-chips">
              {deliverables.map((item, index) => (
                <li
                  key={item}
                  className={`map-reveal ${index % 2 === 0 ? 'map-reveal--left' : 'map-reveal--right'}`}
                  style={{ transitionDelay: `${index * 50}ms` }}
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="map-showcase map-section--muted" aria-labelledby="map-showcase-title">
          <div className="map-shell">
            <div className="map-section__header map-reveal map-reveal--up">
              <p className="map-kicker">Apps we ship</p>
              <h2 id="map-showcase-title">Real products on phones people use</h2>
              <p>
                From marketplace booking apps to EV charging platforms—built once in React Native,
                released for both App Store and Google Play.
              </p>
            </div>
            <div className="map-showcase__grid">
              {SHOWCASE_APPS.map((app) => (
                <article
                  key={app.id}
                  className={`map-showcase__card map-showcase__card--${app.id} map-reveal map-reveal--${app.reveal}`}
                >
                  <div className={`map-showcase__screens map-showcase__screens--${app.screens.length}`}>
                    {app.screens.map((screen) => (
                      <div
                        key={screen.src}
                        className={`map-showcase__device map-device__frame map-device__frame--${screen.frame}`}
                      >
                        {screen.frame === 'ios' ? (
                          <div className="map-device__notch" aria-hidden="true" />
                        ) : null}
                        <img src={screen.src} alt={screen.alt} loading="lazy" />
                        {screen.frame === 'android' ? (
                          <div className="map-device__nav" aria-hidden="true" />
                        ) : (
                          <div className="map-device__home" aria-hidden="true" />
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="map-showcase__body">
                    <h3>{app.title}</h3>
                    <p>{app.blurb}</p>
                    <ul className="map-showcase__tags" aria-label={`${app.title} highlights`}>
                      {app.tags.map((tag) => (
                        <li key={tag}>{tag}</li>
                      ))}
                    </ul>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="map-section" aria-labelledby="map-faq-title">
          <div className="map-shell">
            <div className="map-section__header map-reveal map-reveal--up">
              <p className="map-kicker">FAQ</p>
              <h2 id="map-faq-title">Frequently asked questions</h2>
            </div>
            <div className="map-faq">
              {faqs.map((item, index) => (
                <article
                  key={item.question}
                  className={`map-faq__item map-reveal ${
                    index % 2 === 0 ? 'map-reveal--left' : 'map-reveal--right'
                  }`}
                  style={{ transitionDelay: `${(index % 4) * 60}ms` }}
                >
                  <h3>{item.question}</h3>
                  <p>{item.answer}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="map-related" aria-labelledby="map-related-title">
          <div className="map-shell">
            <h2 id="map-related-title" className="map-reveal map-reveal--up">
              Related services
            </h2>
            <nav className="map-related__links" aria-label="Related service pages">
              {relatedLinks.map((link, index) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`map-related__link map-reveal ${
                    index % 2 === 0 ? 'map-reveal--left' : 'map-reveal--right'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        </section>

        <section className="map-cta" aria-labelledby="map-cta-title">
          <div className="map-shell map-cta__inner map-reveal map-reveal--up">
            <div>
              <p className="map-kicker map-kicker--on-dark">Next step</p>
              <h2 id="map-cta-title">Ready to ship on iOS and Android?</h2>
              <p>Tell us about your users, integrations, and launch timeline.</p>
            </div>
            <div className="map-cta__actions">
              <Link to={primaryCta.to} className="map-btn map-btn--primary">
                {primaryCta.label}
              </Link>
              <Link to={secondaryCta.to} className="map-btn map-btn--on-dark">
                {secondaryCta.label}
              </Link>
            </div>
          </div>
        </section>

        <Newsletters />
      </main>
    </>
  );
}

export default MobileApp;
